import type { ReactNode } from 'react';

import reactReplacePlaceholder from 'react-replace-placeholder';

import {
  BpkAccordion,
  BpkAccordionItem,
  withAccordionItemState,
} from '@skyscanner/backpack-web/bpk-component-accordion';
import BpkLink from '@skyscanner/backpack-web/bpk-component-link';
import { TEXT_STYLES } from '@skyscanner/backpack-web/bpk-component-text';

import FAQMarkup from './Markup/FAQMarkup';

import type { Configs } from '@shared/types/configs';
import type { FaqServiceItems } from '@shared/types/faq-service';

import STYLES from './FAQs.module.scss';

import { FormattedDistance } from '@shared/ui/components/i18n';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';

type Props = {
  title?: ReactNode;
  items: FaqServiceItems;
  configs: Configs;
};

type TextType = Array<{
  name: string;
  href: string;
}>;

const StatefulAccordionItem = withAccordionItemState(BpkAccordionItem);

const strongAndLinkReplacement = (context: string) => {
  const replacements: any = {};
  if (context.includes('{strong}') && context.includes('{/strong}')) {
    replacements.strong = {
      pair: '/strong',
      format: (text: string) => <span style={{ fontWeight: 700 }}>{text}</span>,
    };
  }
  if (context.includes('{a}') && context.includes('{/a}')) {
    replacements.a = {
      pair: '/a',
      format: (text: TextType) => {
        const [name, href] = text.join().split('|');
        return (
          <BpkLink className={STYLES.FAQs__linkItem} href={href}>
            {name}
          </BpkLink>
        );
      },
    };
  }
  if (context.includes('{distance}') && context.includes('{/distance}')) {
    replacements.distance = {
      pair: '/distance',
      format: (text: string) => {
        const distance = parseInt(text, 10);
        return <FormattedDistance distanceMeters={distance} />;
      },
    };
  }
  return JSON.stringify(replacements) === '{}'
    ? context
    : reactReplacePlaceholder(context, replacements, '{', '}');
};

const FAQs = ({ configs, items, title }: Props) => {
  if (!configs.enableSeoFaqsDisplay) {
    return null;
  }

  return (
    <div className={STYLES.FAQs}>
      {title}
      <BpkAccordion>
        {items.map(({ answer, question }, index) => (
          <StatefulAccordionItem
            key={question}
            className={STYLES.FAQs__accordionItem}
            title={strongAndLinkReplacement(question)}
            id={`accordion-item-${index}`}
            tagName="h3"
            textStyle={TEXT_STYLES.heading4}
          >
            {strongAndLinkReplacement(answer)}
          </StatefulAccordionItem>
        ))}
      </BpkAccordion>
      <FAQMarkup questions={items} />
    </div>
  );
};

export default withConfiguration(FAQs);
